<app-section-card title="Image Settings" [collapsed]="false">
	<div class="px-3 py-2.5 flex flex-col gap-2">
		<!-- DEV-655: the only way to look at an image. The centre player runs
		     chapters, so an image has never had a viewer of its own.

		     An anchor rather than a <button> on purpose. The editor wraps every
		     form in `<fieldset [disabled]="isImportedProject()">`, which would
		     take a real button down with it — and rendering writes nothing, so
		     there is no reason an imported project should not be looked at. It
		     is also where the need is sharpest: every project in the dev data
		     that has project-level images is an imported one. This follows the
		     convention already stated in `editor.scss` — navigation stays
		     clickable under the lock because it is not a form control; the
		     fieldset exists to stop data entry. `role`/`tabindex`/`keydown`
		     keep it operable from the keyboard, which the plain clickable divs
		     elsewhere are not. -->
		<div class="flex flex-col gap-1">
			<div>
				<a
					class="add-item-btn"
					role="button"
					[class.add-item-btn--disabled]="!!previewBlocker()"
					[attr.tabindex]="previewBlocker() ? -1 : 0"
					[attr.aria-disabled]="!!previewBlocker()"
					[title]="previewBlocker() ?? 'Render this image on its own canvas and view it'"
					(click)="requestPreview()"
					(keydown.enter)="requestPreview($event)"
					(keydown.space)="requestPreview($event)"
				>
					<i class="pi pi-image text-[0.7rem]"></i>
					Render preview
				</a>
			</div>
			@if (previewBlocker(); as why) {
				<small class="text-[0.7rem] text-wb-muted leading-snug">{{ why }}</small>
			}
		</div>
		<div class="form-row">
			<div class="form-field">
				<label class="form-label">Slug</label>
				<input
					pInputText
					class="w-full font-mono"
					[value]="data().slug ?? ''"
					(change)="onSlugChange($any($event.target).value)"
				/>
			</div>
			<div class="form-field">
				<label class="form-label">Name</label>
				<input
					pInputText
					class="w-full"
					[value]="data().name ?? ''"
					(input)="patch(d => d.name = $any($event.target).value)"
				/>
			</div>
		</div>
		<div class="form-row">
			<div class="form-field form-field--check">
				<p-checkbox
					inputId="image-thumbnail-check"
					[binary]="true"
					[ngModel]="data().thumbnail ?? false"
					(ngModelChange)="patch(d => d.thumbnail = $event)"
				/>
				<label class="form-label" for="image-thumbnail-check">Thumbnail</label>
			</div>
			<div class="form-field form-field--check">
				<p-checkbox
					inputId="image-mask-check"
					[binary]="true"
					[ngModel]="data().mask ?? false"
					(ngModelChange)="setMask($event)"
				/>
				<label class="form-label" for="image-mask-check">Mask</label>
			</div>
		</div>
	</div>
</app-section-card>

@if (data().background) {
	<app-section-card title="Background" [collapsed]="false">
		<div class="px-3 py-2.5 flex flex-col gap-2">
			<div class="form-row">
				<div class="form-field flex-1">
					<label class="form-label">Media</label>
					<div class="picker-row">
						<input
							pInputText
							class="flex-1 text-xs"
							readonly
							[value]="fileName(data().background?.source?.src)"
							[title]="data().background?.source?.src ?? ''"
						/>
						<button class="picker-btn" type="button" (click)="openBgPicker()" title="Browse files">
							<i class="pi pi-folder-open text-xs"></i>
						</button>
						@if (data().background?.source?.src) {
							<button class="picker-btn" type="button" (click)="clearBgSrc()" title="Clear media">
								<i class="pi pi-times text-xs"></i>
							</button>
						}
						@if (canGenerate() && !generateStatus()) {
							<button class="picker-btn" type="button" (click)="startGenerate()" [title]="hasVersions() ? 'Re-generate versions' : 'Generate versions'">
								@if (hasVersions()) {
									<i class="pi pi-check text-xs" style="color: var(--wb-success)"></i>
								} @else {
									<i class="pi pi-cog text-xs"></i>
								}
							</button>
						}
					</div>
					@if (generateStatus(); as job) {
						<div class="generate-status" [class.generate-status--error]="job.status === 'error'">
							@if (job.status === 'error') {
								<i class="pi pi-exclamation-triangle text-[10px]"></i>
								<span class="flex-1 truncate">{{ job.error ?? 'Failed' }}</span>
								<button class="generate-dismiss" type="button" (click)="dismissGenerateStatus()" title="Dismiss">
									<i class="pi pi-times text-[8px]"></i>
								</button>
							} @else {
								<i class="pi pi-spin pi-spinner text-[10px]"></i>
								<span class="flex-1 truncate">{{ job.currentStep }} ({{ job.completedVersions.length }}/{{ job.totalVersions || '?' }})</span>
							}
						</div>
					}
				</div>
			</div>
			<div class="form-row">
				<div class="form-field form-field">
					<label class="form-label">Data Type</label>
					<p-select
						[options]="bgDataTypeOptions"
						optionValue="value"
						appendTo="body"
						[ngModel]="data().background?.source?.dataType ?? 'IMAGE'"
						(ngModelChange)="setBgField('dataType', $event)"
						styleClass="p-select-fluid"
					/>
				</div>
			</div>
		</div>
	</app-section-card>
}

<app-section-card title="Dimensions" [collapsed]="true">
	<div class="px-3 py-2.5 flex flex-col gap-2">
		@if (imageRef()) {
			<p class="text-[0.7rem] text-wb-muted leading-snug">
				Render size for this image, stored on its reference in <code>project.json</code>.
				Leave empty to inherit the project's size. <strong>Width picks which generated
				file is downloaded</strong>; height only shapes the canvas — so a shorter height
				at the same width gives one image its own aspect ratio.
			</p>
			<div class="form-row">
				<div class="form-field">
					<label class="form-label">Width</label>
					<p-inputnumber
						[ngModel]="draft().width"
						(ngModelChange)="setRefDimension('width', $event)"
						[min]="0"
						[showButtons]="false"
						styleClass="p-inputnumber-fluid"
						placeholder="Inherited"
					/>
				</div>
				<div class="form-field">
					<label class="form-label">Height</label>
					<p-inputnumber
						[ngModel]="draft().height"
						(ngModelChange)="setRefDimension('height', $event)"
						[min]="0"
						[showButtons]="false"
						styleClass="p-inputnumber-fluid"
						placeholder="Inherited"
					/>
				</div>
			</div>
			@if (sizeWarning(); as warning) {
				<small class="text-red-400 block">{{ warning }}</small>
			}
			@if (widthWarning(); as warning) {
				<small class="text-amber-400 block">{{ warning }}</small>
			}
			<div class="form-row">
				<div class="form-field">
					<label class="form-label">Orig Coords Width</label>
					<p-inputnumber
						[ngModel]="draft().origCoordsWidth"
						(ngModelChange)="setRefOrigCoordsWidth($event)"
						[min]="0"
						[showButtons]="false"
						styleClass="p-inputnumber-fluid"
						placeholder="Inherited"
					/>
				</div>
			</div>
			@if (hasDimensionsOverride()) {
				<div>
					<button class="text-[0.75rem] text-wb-accent hover:text-wb-accent-2 transition-colors bg-transparent border-0 cursor-pointer p-0" type="button" (click)="resetRefDimensions()">
						Reset to the project's size
					</button>
				</div>
			}
		} @else {
			<p class="text-[0.7rem] text-wb-muted leading-snug">
				This image has no reference in <code>project.json</code>, so it has no render
				size to set — the player can only reach an image through a reference.
			</p>
		}
	</div>
</app-section-card>

<app-content-items-form
	[content]="data().content ?? []"
	[aeComp]="aeComp()"
	(contentChange)="onContentChange($event)"
	(copyItem)="copyItem.emit($event)"
/>

<app-media-picker
	[(visible)]="pickerVisible"
	[categories]="pickerCategories()"
	[currentValue]="data()?.background?.source?.src ?? ''"
	(fileSelected)="setBgSrc($event)"
/>
